import * as React from "react";
import { Text } from "@fluentui/react/lib/Text";
import { TextField } from "@fluentui/react/lib/TextField";
import { Icon } from "@fluentui/react/lib/Icon";
import { DefaultButton } from "@fluentui/react/lib/Button";
import type { QuotedRate } from "@src/types/api";

const formatCurrency = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const roundMoney = (n: number) => Math.round(n * 100) / 100;
/** Margin as fraction of sell price; keep extra precision so % ↔ final round-trips after Apply. */
const roundMarginFraction = (f: number) => Math.round(Math.max(0, Math.min(0.99, f)) * 1e6) / 1e6;

function asFraction(raw: unknown): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return 0;
  if (raw > 1 && raw <= 100) return Math.min(0.99, raw / 100);
  if (raw > 100) return 0.99;
  return Math.max(0, Math.min(0.99, raw));
}

function finalFromMarginPercent(appliedCost: number, marginPercent: number): number {
  const m = Math.max(0, Math.min(99, marginPercent)) / 100;
  const denom = 1 - m;

  if (denom <= 0) return roundMoney(appliedCost);
  return roundMoney(appliedCost / denom);
}

function fractionFromDollarMode(appliedCost: number, marginDollars: number): number {
  const final = roundMoney(appliedCost + Math.max(0, marginDollars));

  if (final <= 0) return 0;
  return Math.min(0.99, (final - appliedCost) / final);
}

const labelStyle: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 600,
  color: "#605e5c",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  marginBottom: "6px",
};
const segmentBtn = (active: boolean): React.CSSProperties => ({
  minWidth: 36,
  padding: "6px 10px",
  border: "1px solid #c8c6c4",
  background: active ? "#edebe9" : "#fff",
  fontWeight: active ? 600 : 400,
  fontSize: "13px",
  cursor: "pointer",
  borderRadius: "4px 0 0 4px",
});

export type CustomerOfferPatch = {
  appliedCost: number;
  appliedMargin: number;
  totalBilledRate: number;
  appliedMarkupPercent: number;
};

interface Props {
  offer: QuotedRate | null;
  enabled?: boolean;
  onApply: (patch: CustomerOfferPatch) => void;
}

export default function CustomerOffer({ offer, enabled = true, onApply }: Props) {
  const [marginMode, setMarginMode] = React.useState<"percent" | "dollar">("percent");
  const [costInput, setCostInput] = React.useState("");
  const [marginPercentStr, setMarginPercentStr] = React.useState("");
  const [marginDollarStr, setMarginDollarStr] = React.useState("");
  const calculatedFrac =
    offer && offer.calculatedMargin != null ? asFraction(offer.calculatedMargin) : null;
  const suggested = Number(offer?.suggestedCost ?? 0) || 0;

  React.useEffect(() => {
    if (!offer) {
      setCostInput("");
      setMarginPercentStr("");
      setMarginDollarStr("");
      return;
    }
    const sug = Number(offer.suggestedCost ?? 0) || 0;

    const ac = roundMoney(Number(offer.appliedCost ?? sug) || 0);

    setCostInput(ac > 0 ? String(ac) : "");
    // Prefer explicit applied margin, then API marginPct, then calculated margin.
    const marginFrac = asFraction(
      offer.appliedMargin ?? offer.marginPct ?? offer.calculatedMargin ?? 0
    );
    const pctDisplay = roundMoney(marginFrac * 100);
    const explicitMarkup =
      (typeof offer.appliedMargin === "number" && Number.isFinite(offer.appliedMargin)) ||
      (typeof offer.marginPct === "number" && Number.isFinite(offer.marginPct)) ||
      (typeof offer.calculatedMargin === "number" && Number.isFinite(offer.calculatedMargin));

    setMarginPercentStr(pctDisplay > 0 ? String(pctDisplay) : explicitMarkup ? "0" : "");
    const fin = finalFromMarginPercent(ac, pctDisplay);

    setMarginDollarStr(fin > ac ? String(roundMoney(fin - ac)) : "");
  }, [
    offer?.id,
    offer?.appliedCost,
    offer?.suggestedCost,
    offer?.appliedMargin,
    offer?.marginPct,
    offer?.calculatedMargin,
  ]);
  const appliedCostNum = (() => {
    const n = parseFloat(costInput.replace(/,/g, ""));

    return Number.isFinite(n) ? Math.max(0, n) : 0;
  })();
  const marginPercentPoints = (() => {
    const n = parseFloat(marginPercentStr);

    return Number.isFinite(n) && n >= 0 ? n : 0;
  })();
  const marginDollarsNum = (() => {
    const n = parseFloat(marginDollarStr.replace(/,/g, ""));

    return Number.isFinite(n) ? Math.max(0, n) : 0;
  })();

  const finalOffer =
    marginMode === "percent"
      ? finalFromMarginPercent(appliedCostNum, marginPercentPoints)
      : roundMoney(appliedCostNum + marginDollarsNum);

  const appliedMarginFrac =
    marginMode === "percent"
      ? Math.max(0, Math.min(0.99, marginPercentPoints / 100))
      : fractionFromDollarMode(appliedCostNum, marginDollarsNum);
  const handleApply = React.useCallback(() => {
    if (!offer || !enabled) return;
    const appliedMarkupPercent =
      marginMode === "percent"
        ? roundMoney(marginPercentPoints)
        : appliedCostNum > 0
          ? roundMoney((100 * marginDollarsNum) / appliedCostNum)
          : 0;

    onApply({
      appliedCost: roundMoney(appliedCostNum),
      appliedMargin: roundMarginFraction(appliedMarginFrac),
      totalBilledRate: roundMoney(finalOffer),
      appliedMarkupPercent,
    });
  }, [
    offer,
    enabled,
    onApply,
    appliedCostNum,
    appliedMarginFrac,
    finalOffer,
    marginMode,
    marginPercentPoints,
    marginDollarsNum,
  ]);

  if (!offer) {
    return (
      <BoxSection title="Customer offer">
        <Text styles={{ root: { fontSize: "12px", color: "#605e5c" } }}>
          Run a quote to set customer pricing.
        </Text>
      </BoxSection>
    );
  }

  return (
    <BoxSection title="Customer offer">
      <div style={{ marginBottom: "16px" }}>
        <div style={labelStyle}>Expected cost</div>
        <TextField
          value={costInput}
          disabled={!enabled}
          onChange={(_, v) => setCostInput(v ?? "")}
          styles={{ fieldGroup: { borderRadius: 6 } }}
        />
        {suggested > 0 ? (
          <Text
            variant="small"
            styles={{ root: { fontSize: "11px", color: "#a19f9d", marginTop: "4px" } }}
          >
            Suggested (blend): {formatCurrency(suggested)}
          </Text>
        ) : null}
      </div>

      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <span style={labelStyle}>Margin</span>
          {calculatedFrac != null ? (
            <span
              style={{
                fontSize: "11px",
                color: "#a19f9d",
                textTransform: "none",
                letterSpacing: 0,
                fontWeight: 400,
              }}
            >
              Target {roundMoney(calculatedFrac * 100)}% (calculated)
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <div style={{ display: "flex" }}>
            <button
              type="button"
              style={{
                ...segmentBtn(marginMode === "dollar"),
                borderRadius: "4px 0 0 4px",
              }}
              onClick={() => setMarginMode("dollar")}
            >
              $
            </button>
            <button
              type="button"
              style={{
                ...segmentBtn(marginMode === "percent"),
                borderRadius: "0 4px 4px 0",
                borderLeft: "none",
              }}
              onClick={() => setMarginMode("percent")}
            >
              %
            </button>
          </div>
          {marginMode === "percent" ? (
            <>
              <TextField
                styles={{
                  fieldGroup: { width: 72, borderRadius: 6 },
                  field: { textAlign: "right" as const },
                }}
                value={marginPercentStr}
                disabled={!enabled}
                onChange={(_, v) => setMarginPercentStr(v ?? "")}
              />
              <Text styles={{ root: { fontSize: "13px", color: "#323130" } }}>%</Text>
              <Stepper
                disabled={!enabled}
                onDelta={(d) => {
                  const cur = parseFloat(marginPercentStr) || 0;
                  const next = Math.max(0, roundMoney(cur + d));

                  setMarginPercentStr(String(next));
                }}
              />
            </>
          ) : (
            <>
              <TextField
                styles={{
                  fieldGroup: { width: 96, borderRadius: 6 },
                  field: { textAlign: "right" as const },
                }}
                value={marginDollarStr}
                disabled={!enabled}
                onChange={(_, v) => setMarginDollarStr(v ?? "")}
              />
              <Stepper
                disabled={!enabled}
                onDelta={(d) => {
                  const cur = marginDollarsNum;
                  const next = Math.max(0, roundMoney(cur + d));

                  setMarginDollarStr(String(next));
                }}
              />
            </>
          )}
        </div>
      </div>

      <div>
        <div style={labelStyle}>Final offer</div>
        <div
          style={{
            padding: "10px 12px",
            borderRadius: 6,
            background: "#f3f2f1",
            border: "1px solid #edebe9",
            fontSize: "18px",
            fontWeight: 700,
            color: "#323130",
          }}
        >
          {formatCurrency(finalOffer)}
        </div>
        <div style={{ marginTop: "14px" }}>
          <DefaultButton
            text="Apply"
            type="button"
            onClick={handleApply}
            disabled={!enabled}
            allowDisabledFocus
            styles={{
              root: { minWidth: 96 },
            }}
          />
        </div>
      </div>
    </BoxSection>
  );
}

function BoxSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "16px 18px",
        marginBottom: "16px",
        border: "1px solid #edebe9",
        borderRadius: "8px",
        background: "#fff",
      }}
    >
      <Text
        variant="medium"
        styles={{
          root: {
            fontSize: "13px",
            fontWeight: 600,
            color: "#323130",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: "12px",
            display: "block",
          },
        }}
      >
        {title}
      </Text>
      {children}
    </div>
  );
}

function Stepper({ disabled, onDelta }: { disabled?: boolean; onDelta: (delta: number) => void }) {
  const btn: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    padding: 0,
    border: "1px solid #c8c6c4",
    borderRadius: 2,
    background: "#fff",
    cursor: disabled ? "default" : "pointer",
    color: "#323130",
  };

  return (
    <div style={{ display: "flex", gap: 4 }}>
      <button type="button" disabled={disabled} style={btn} onClick={() => onDelta(0.5)}>
        <Icon iconName="ChevronUp" styles={{ root: { fontSize: 12 } }} />
      </button>
      <button type="button" disabled={disabled} style={btn} onClick={() => onDelta(-0.5)}>
        <Icon iconName="ChevronDown" styles={{ root: { fontSize: 12 } }} />
      </button>
    </div>
  );
}
